import '.d{display:flex;}.ssr-plugin'
import '.ah{justify-content:center;}.ssr-plugin'
import '.t{align-items:center;}.ssr-plugin'
import '.fa{background-color:var(--palette-blue-50);}.ssr-plugin'
import '.e0{color:var(--palette-blue-300);}.ssr-plugin'
import '.fj{border-radius:0.5rem;}.ssr-plugin'
import '.j2{width:2rem;}.ssr-plugin'
import '.bc{height:2rem;}.ssr-plugin'
import '.ad{aspect-ratio:1/1;}.ssr-plugin'
import '.aa{min-width:0px;}.ssr-plugin'
import '.b3{overflow:hidden;}.ssr-plugin'
import { Text } from '../typography/Text'
import { IconButton } from '../button/IconButton'
import {
  faXmark,
  faImage,
  faCircleNotch,
  IconDefinition,
} from '@fortawesome/pro-regular-svg-icons'
import { faFilePdf } from '@fortawesome/pro-solid-svg-icons'
import { styled } from 'inlines'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Image } from '../image/Image'

export type FileDisplayProps = {
  type: 'document' | 'image'
  name: string
  src?: string
  fileSize?: number
  onRemove?: VoidFunction
  isLoading?: boolean
}

const iconVariant: Record<FileDisplayProps['type'], IconDefinition> = {
  image: faImage,
  document: faFilePdf,
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const primaryBlue = 'var(--Primary-Blue, #130F33)'

const fileDisplayStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.5rem',
  border: 'var(--border-subtle)',
  backgroundColor: 'var(--palette-white)',
  boxShadow: '0 3px 10px 0 rgba(0, 0, 0, 0.05)',
  borderRadius: '0.625rem',
  textDecoration: 'none',
  color: primaryBlue,
  cursor: 'pointer',
  '&:link, &:visited, &:active': {
    color: primaryBlue,
  },
  '&:hover': {
    backgroundColor: 'var(--gray-100)',
    color: primaryBlue,
  },
}

export const FileDisplay = ({
  type,
  name,
  src,
  fileSize,
  onRemove,
  isLoading = false,
}: FileDisplayProps) => {
  const content = (
    <>
      {type === 'image' && src && !isLoading ? (
        <Image
          src={src}
          alt={name}
          resizeWidth={32}
          style={{
            width: '2rem',
            height: '2rem',
            objectFit: 'cover',
            borderRadius: '0.5rem',
          }}
        />
      ) : (
        <div className="d ah t fa e0 fj j2 bc ad"
        >
          {isLoading ? (
            <FontAwesomeIcon
              icon={faCircleNotch}
              spin
              style={{ fontSize: '1rem' }}
            />
          ) : (
            <FontAwesomeIcon
              icon={iconVariant[type]}
              style={
                type === 'document'
                  ? { color: 'var(--Shades-Blue-500, #525785)' }
                  : undefined
              }
            />
          )}
        </div>
      )}
      <div className="aa b3">
        <Text.Small
          isBold
          color={primaryBlue}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {name}
        </Text.Small>
        {fileSize !== undefined && (
          <Text.ExtraSmall
            style={{
              color: 'var(--gray-300)',
              display: 'block',
            }}
          >
            {formatFileSize(fileSize)}
          </Text.ExtraSmall>
        )}
      </div>
    </>
  )

  if (src) {
    return (
      <styled.a
        href={src}
        download={name}
        style={fileDisplayStyle}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          if (onRemove) {
            e.stopPropagation()
          }
        }}
      >
        {content}
        {onRemove && !isLoading && (
          <div className="d t"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
              onRemove()
            }}
          >
            <IconButton
              size="small"
              variant="transparent"
              icon={faXmark}
              onClick={() => {}}
              style={{
                border: 0,
                width: '1rem',
                height: '1rem',
              }}
            />
          </div>
        )}
      </styled.a>
    )
  }

  return (
    <styled.div style={fileDisplayStyle}>
      {content}
      {onRemove && !isLoading && (
        <IconButton
          size="small"
          variant="transparent"
          icon={faXmark}
          onClick={onRemove}
          style={{
            border: 0,
            width: '1rem',
            height: '1rem',
          }}
        />
      )}
    </styled.div>
  )
}
